import { useCallback, useContext, useEffect } from 'react'

import { MediaViewerContext, MediaViewerItem } from '@place/media-viewer'
import { useRouter } from 'next/router'
import { useEffectOnce } from 'react-use'

import { gql } from '@web/gql'
import { UseOpenMediaViewer_UserProfileFragment } from '@web/gql/graphql'
import useLockBodyScroll from '@web/hooks/useLockBodyScroll'
import { BASE_PATH } from '@web/libs/constants'

const useOpenMediaViewer = () => {
  const { openMediaViewer: openPlaceMediaViewer, isOpened } = useContext(MediaViewerContext)
  const router = useRouter()

  useLockBodyScroll({ needLock: isOpened })

  useEffect(() => {
    if (!isOpened) return

    // If viewer opened, disable popstate scroll reset when back from viewer
    router.beforePopState(({ as }) => {
      if (as === `${BASE_PATH}${router.asPath}`) return false
      return true
    })
  }, [router, isOpened])

  useEffectOnce(() => {
    return () => router.beforePopState(() => true)
  })

  const openMediaViewer = useCallback(
    (index: number, mediaViewerItems: MediaViewerItem[]) => {
      openPlaceMediaViewer(mediaViewerItems, index, true, false)
    },
    [openPlaceMediaViewer],
  )

  return openMediaViewer
}

export const convertProfileImageToViewer = (
  profile: UseOpenMediaViewer_UserProfileFragment,
): MediaViewerItem | null => {
  if (!profile.imageUrl) return null

  const INDEX = 0
  return {
    originalUrl: profile.originImageUrl ?? profile.imageUrl,
    imgUrl: profile.originImageUrl ?? profile.imageUrl,
    no: INDEX,
    id: INDEX.toString(),
  }
}

useOpenMediaViewer.fragment = gql(`
  fragment useOpenMediaViewer_MediaItem on MediaItem {
    __typename
    id
    type
    ... on ImageItem {
      url
    }
    ... on VideoItem {
      thumbnail
      playback {
        url
      }
      isIllegalVideo
    }
  }

  fragment useOpenMediaViewer_UserProfile on Profile {
    imageUrl
    originImageUrl
  }

  fragment useOpenMediaViewer_ReviewGroup on ReviewGroup {
    id
    author {
      id
      profile {
        nickname
        imageUrl
        border {
          name
          imageUrl
        }
      }
      isFriend
    }
    text
    rating
    visitedDateTime
    selectedProof {
      id
      type
    }
    votedKeywords {
      text {
        korean
      }
      code
      icon
    }
    place {
      visitCategories {
        subTitle {
          korean
        }
        keywords {
          code
          text {
            korean
          }
        }
      }
    }
    visitKeywords {
      code
    }
    visit {
      id
      visitCount
    }
    mediaItems {
    ...useOpenMediaViewer_MediaItem
    }
    
  }
`)

export default useOpenMediaViewer
